import { ref } from 'vue';
import { MAINTENANCE_MESSAGE } from '@/utils/constants';

// このページは、TOPページ（ログイン前 / 後）のお知らせ表示とはまた別途、ログイン画面/会員登録画面用のお知らせ表示UIです。表示切替えは手動でtrue/falseで行う

// メンテナンス情報の型定義
export interface MaintenanceInfo {
  isActive: boolean; // メンテナンス情報の表示可否フラグ
  startDate: string; // 開始日付
  startTime: string; // 開始時間
  endTime: string; // 終了時間
  additionalInfo?: string; // 追加情報
  id: string; // お知らせの一意ID (閉じるために使用)
  isCollapsed?: boolean; // アコーディオンが閉じているかどうか
}

// お知らせの型定義
export interface AnnouncementInfo {
  id: number;
  title: string;
  message: string;
  additionalInfo?: string;
  startDate?: string;
  endDate?: string;
  isVisible: boolean;
  isCollapsed?: boolean; // アコーディオンが閉じているかどうか
}

// 曜日の配列（0=日曜日, 1=月曜日, ... 6=土曜日）
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

// Cookie管理のユーティリティ
const cookieUtils = {
  // Cookieを設定する
  setCookie: (name: string, value: string): void => {
    // 翌日の0時に期限が切れるように設定
    const tomorrow = new Date();
    tomorrow.setHours(24, 0, 0, 0); // 翌日の0時に設定
    const expires = `expires=${tomorrow.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  },

  // Cookieを取得する
  getCookie: (name: string): string | null => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split('=');
      if (cookieName === name) {
        return cookieValue;
      }
    }
    return null;
  },

  // お知らせが閉じられているかをチェック
  isAnnouncementCollapsed: (id: string | number): boolean => {
    return cookieUtils.getCookie(`${id}_state`) === 'collapsed';
  },

  // お知らせの状態を保存
  saveAnnouncementState: (id: string | number, isCollapsed: boolean): void => {
    cookieUtils.setCookie(`${id}_state`, isCollapsed ? 'collapsed' : 'open');
  },

  // お知らせのCookieをすべてクリアする
  clearAnnouncementCookies: (): void => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.endsWith('_state')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    }
    console.log('お知らせ状態をリセットしました。すべてのお知らせが展開されます。');
  },
};

// メンテナンス設定（ここを編集するだけでメンテナンス情報を変更可能）
const maintenanceConfig = {
  // アクティブ状態（手動で切替え、true=表示、false=非表示）
  // active: false,
  active: true,
  // ID (閉じるために使用)
  id: 'maintenance',
  // メンテナンス日時（実際の月をそのまま使用）
  // weekdayは日付から自動的に計算
  year: 2025,
  month: 5,
  day: 4,
  startHour: 10,
  startMinute: 0,
  endHour: 14,
  endMinute: 0,
  // 追加メッセージ
  message: MAINTENANCE_MESSAGE.ADDITIONAL_INFO,
};

// メンテナンス情報のキャッシュ
const maintenanceInfoCache = ref<MaintenanceInfo>({
  isActive: false,
  startDate: '',
  startTime: '',
  endTime: '',
  additionalInfo: '',
  id: maintenanceConfig.id,
  isCollapsed: false,
});

// お知らせ情報のキャッシュ
const announcementsCache = ref<AnnouncementInfo[]>([]);

// メンテナンス日付を表すDateオブジェクトを取得
const getMaintenanceDate = (): Date => {
  return new Date(
    maintenanceConfig.year,
    maintenanceConfig.month - 1, // JavaScriptのDateは0始まり
    maintenanceConfig.day
  );
};

// メンテナンス終了日時（JavaScriptのDateオブジェクト形式）
const getMaintenanceEndDate = (): Date => {
  return new Date(
    maintenanceConfig.year,
    maintenanceConfig.month - 1, // JavaScriptのDateは0始まり
    maintenanceConfig.day,
    maintenanceConfig.endHour,
    maintenanceConfig.endMinute
  );
};

// 日付の曜日を取得
const getWeekday = (date: Date): string => {
  return WEEKDAYS[date.getDay()];
};

// 表示用の日付文字列を生成
const getDisplayDate = (): string => {
  const date = getMaintenanceDate();
  const weekday = getWeekday(date);
  return `${maintenanceConfig.year}年${maintenanceConfig.month}月${maintenanceConfig.day}日（${weekday}）`;
};

// 表示用の時間文字列を生成
const getDisplayTime = (hour: number, minute: number): string => {
  return `${hour}：${minute === 0 ? '00' : minute}`;
};

/**
 * メンテナンス情報が現在アクティブかどうかをチェック
 * メンテナンスの終了時刻を過ぎていれば自動的に非表示になる
 */
const isMaintenanceActive = (): boolean => {
  // 手動でアクティブ状態が無効化されていれば非表示
  if (!maintenanceConfig.active) {
    return false;
  }

  // 現在時刻が終了時刻を過ぎていれば非表示
  const now = new Date();
  const endDate = getMaintenanceEndDate();
  return now < endDate;
};

/**
 * メンテナンス情報を取得する
 * メンテナンスの状態を動的に判断して返す
 */
export const getMaintenanceInfo = async (): Promise<MaintenanceInfo> => {
  const isActive = isMaintenanceActive();
  const isCollapsed = cookieUtils.isAnnouncementCollapsed(maintenanceConfig.id);

  const maintenanceInfo = {
    isActive: isActive, // アコーディオン形式に変更したので常に表示
    startDate: getDisplayDate(),
    startTime: getDisplayTime(maintenanceConfig.startHour, maintenanceConfig.startMinute),
    endTime: getDisplayTime(maintenanceConfig.endHour, maintenanceConfig.endMinute),
    additionalInfo: maintenanceConfig.message,
    id: maintenanceConfig.id,
    isCollapsed: isCollapsed,
  };

  // キャッシュを更新
  maintenanceInfoCache.value = maintenanceInfo;

  return maintenanceInfo;
};

/**
 * お知らせ一覧を取得する
 * 実装を単純化：固定のお知らせリストを返す
 */
export const getAnnouncements = async (): Promise<AnnouncementInfo[]> => {
  // 固定のお知らせリスト（必要に応じて変更）
  const announcements: AnnouncementInfo[] = [
    {
      isVisible: true, // 表示可否フラグ
      id: 1, // お知らせID
      title: 'ゴールデンウィーク期間中の休業について', // お知らせタイトル
      message: `<div><span>誠に勝手ながら、下記の期間をゴールデンウィーク休業とさせていただきます。</span>
        <span class="fw-bold size-16">2025年5月3日（土）～5月6日（火）</span>
        <span class="mt-0 size-14">休業期間中にいただきましたお問い合わせ等につきましては、<b class="size-16">2025年5月7日（水）</b>より順次対応させていただきます。</span>
        <span class="size-13">ご不便をおかけいたしますが、何卒ご理解のほどよろしくお願い申し上げます。</span>
      </div>`,
      startDate: '', // 表示開始日 ※月日の表記は2桁 / 即時表示なら空欄でOK
      endDate: '2025-05-06', // 表示終了日 ※月日の表記は2桁  日が変われば表示は消えます
      isCollapsed: cookieUtils.isAnnouncementCollapsed(1),
    },
    {
      isVisible: true,
      id: 2,
      title: `【お知らせ】札幌支社がOPEN！道内での転職支援をスムーズに`,
      message: `ご相談は札幌支社での対面面談はもちろん、オンラインでも対応可能です。
      北海道エリアでの転職をお考えの方は、ぜひお気軽にご相談ください。`,
      startDate: '2025-07-08', // 表示開始日 ※月日の表記は2桁 / 即時表示なら空欄でOK
      endDate: '2025-07-31', // 表示終了日 ※月日の表記は2桁  日が変われば表示は消えます
      isCollapsed: cookieUtils.isAnnouncementCollapsed(1),
    },
  ];

  // キャッシュを更新
  announcementsCache.value = announcements;

  return announcements;
};

/**
 * 表示すべきお知らせのみをフィルタリングして取得する
 */
export const getVisibleAnnouncements = async (): Promise<AnnouncementInfo[]> => {
  const allAnnouncements = await getAnnouncements();
  const today = new Date().toISOString().split('T')[0];

  return allAnnouncements.filter(announcement => {
    return (
      announcement.isVisible &&
      (!announcement.startDate || announcement.startDate <= today) &&
      (!announcement.endDate || announcement.endDate >= today)
    );
  });
};

/**
 * お知らせの状態を切り替える（開閉）
 * @param id お知らせID
 * @param collapse 折りたたむか（trueなら折りたたむ、falseなら展開）
 */
export const toggleAnnouncementState = (id: string | number, collapse?: boolean): void => {
  let isCollapsed: boolean;

  if (collapse === undefined) {
    // 現在の状態を反転
    isCollapsed = !cookieUtils.isAnnouncementCollapsed(id);
  } else {
    // 指定された状態に設定
    isCollapsed = collapse;
  }

  // Cookieに保存
  cookieUtils.saveAnnouncementState(id, isCollapsed);

  // メンテナンス情報のキャッシュを更新
  if (maintenanceInfoCache.value && maintenanceInfoCache.value.id === id.toString()) {
    maintenanceInfoCache.value.isCollapsed = isCollapsed;
  }

  // お知らせ情報のキャッシュを更新
  if (announcementsCache.value && announcementsCache.value.length > 0) {
    const announcement = announcementsCache.value.find(a => a.id.toString() === id.toString());
    if (announcement) {
      announcement.isCollapsed = isCollapsed;
    }
  }
};

/**
 * すべてのお知らせをリセット（展開）する
 */
export const resetAllAnnouncements = (): void => {
  // すべてのCookieをクリア
  cookieUtils.clearAnnouncementCookies();

  // メンテナンス情報のキャッシュを更新
  if (maintenanceInfoCache.value) {
    maintenanceInfoCache.value.isCollapsed = false;
  }

  // お知らせ情報のキャッシュを更新
  if (announcementsCache.value && announcementsCache.value.length > 0) {
    announcementsCache.value.forEach(announcement => {
      announcement.isCollapsed = false;
    });
  }
};

export default {
  getMaintenanceInfo,
  getAnnouncements,
  getVisibleAnnouncements,
  toggleAnnouncementState,
  resetAllAnnouncements,
};
